<template>
  <UnvalidLead v-if="unvalidLead" />
  <div v-else class="bg-soft-gray">
    <div v-if="offerPrices.length">
      <Header :discount="lead.discount" :broker-logo-url="brokerLogoUrl" :logo-url="logoUrl" />

      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-4xl">
          <MarketingSection :marketing="marketing" :marketing-details="marketingDetails" />
          <div id="payment" ref="paymentElement">
            <RadioGroup v-model="selectedContractSku">
              <RadioGroupLabel class="text-2xl font-bold">
                <div class="mt-8">
                  {{
                    isVirtualProduct
                      ? "Choisissez votre formule"
                      : `Choisissez votre formule pour votre ${productTitle}`
                  }}
                </div>
              </RadioGroupLabel>
              <div class="flex gap-4 mt-4 flex-col sm:flex-row">
                <OfferPriceOption
                  v-for="offerPrice in offerPrices"
                  :key="offerPrice.contractSku"
                  :offer-price="offerPrice"
                  :offer-prices="offerPrices"
                  :recommended-offer="lead.advisoryAnswer?.recommendedOffer"
                  :is-mpcp-category="isMpcpCategory"
                  :month-discount="monthDiscount"
                  :discount="lead.discount"
                />
              </div>
            </RadioGroup>
          </div>

          <span v-if="monthDiscount && monthlyPrice" class="mt-1 text-gray-500 text-sm">
            * puis {{ formatPriceTTC(monthlyPrice) }}/mois
          </span>

          <div class="mt-8">
            <p class="text-2xl font-bold">Complétez vos informations</p>

            <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div">
              <form @submit="handleSubmit($event, handlePayment)">
                <div class="grid grid-cols-6 gap-x-4 mt-2">
                  <div class="col-span-6 sm:col-span-3">
                    <FormField
                      id="LeadFirstName"
                      name="LeadFirstName"
                      label="Prénom"
                      type="text"
                      placeholder="John"
                      v-model="form.firstName"
                      field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                      label-class="block text-sm text-gray-600"
                    />
                  </div>

                  <div class="col-span-6 sm:col-span-3">
                    <FormField
                      id="LeadLastName"
                      name="LeadLastName"
                      label="Nom"
                      type="text"
                      placeholder="Doe"
                      v-model="form.lastName"
                      field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                      label-class="block text-sm text-gray-600"
                    />
                  </div>

                  <div class="col-span-6 sm:col-span-3">
                    <FormField
                      id="LeadEmail"
                      name="LeadEmail"
                      label="Adresse email"
                      type="email"
                      placeholder="example@gmail.com"
                      v-model="form.email"
                      field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                      label-class="block text-sm text-gray-600"
                    />
                  </div>

                  <div class="col-span-6 sm:col-span-3">
                    <FormField
                      id="LeadPhone"
                      name="LeadPhone"
                      label="Numéro de téléphone"
                      type="tel"
                      placeholder="+ 33 6 10 10 10 10"
                      v-model="form.phone"
                      field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                      label-class="block text-sm text-gray-600"
                    />
                  </div>

                  <template v-if="lead.professionalCustomer">
                    <div class="col-span-6 sm:col-span-3">
                      <FormField
                        id="SirenNumber"
                        name="SirenNumber"
                        label="SIREN"
                        type="text"
                        placeholder="123456789"
                        v-model="form.sirenNumber"
                        field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                        label-class="block text-sm text-gray-600"
                      />
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                      <FormField
                        id="CompanyName"
                        name="CompanyName"
                        label="Nom de l'entreprise"
                        type="text"
                        placeholder="Estaly"
                        v-model="form.companyName"
                        field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                        label-class="block text-sm text-gray-600"
                      />
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                      <FormField
                        id="BuyerRole"
                        name="BuyerRole"
                        label="Rôle du représentant dans l'entreprise"
                        type="text"
                        placeholder="PDG, Responsable commercial..."
                        v-model="form.buyerRole"
                        field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                        label-class="block text-sm text-gray-600"
                      />
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                      <Selector
                        id="LegalForm"
                        name="LegalForm"
                        label="Forme juridique de l'entreprise"
                        :required="true"
                        v-model="form.legalForm"
                        :options="legalFormOptions"
                        field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm ring-gray-400 focus:ring-gray-400"
                        label-class="my-1.5 block text-sm text-gray-600"
                      />
                    </div>
                  </template>

                  <div class="col-span-6 sm:col-span-3">
                    <GoogleAddress :initialAddress="form.address" @newAddress="(value) => (form.address = value)" />
                  </div>

                  <div class="col-span-6 sm:col-span-3">
                    <FormField
                      id="AddressComplement"
                      name="AddressComplement"
                      label="Complément d'adresse"
                      type="text"
                      placeholder="Résidence, Etage..."
                      v-model="form.address.other"
                      field-class="mt-1 block w-full rounded-2xl border-gray-400 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
                      label-class="block text-sm text-gray-600"
                    />
                  </div>
                </div>

                <StripePayment
                  class="mt-8"
                  v-if="selectedOfferPrice"
                  :offerPrice="selectedOfferPrice"
                  :lead="paymentLead"
                />
                <CustomerAgreement :marketingPage="marketingPage" :contractType="contractType" />
              </form>
            </VeeForm>
          </div>
        </div>
      </div>
      <div v-if="!lead.thirdPartyManaged" class="py-12 sm:p-12">
        <Footer />
      </div>
    </div>
    <div class="flex justify-center" v-if="!offerPrices.length">
      <Loading />
    </div>
  </div>
</template>

<script>
import { RadioGroup, RadioGroupLabel } from "@headlessui/vue";
import { Form as VeeForm } from "vee-validate";
import { mapGetters, mapActions } from "vuex";
import StripePayment from "@/components/StripePayment";
import Loading from "@/components/subscription/Loading";
import Footer from "@/components/subscription/Footer";
import CustomerAgreement from "@/components/subscription/CustomerAgreement";
import GoogleAddress from "@/components/GoogleAddress";
import {
  formatPrice,
  formatPriceTTC,
  FormField,
  Selector,
  isVirtualProduct as isVirtualProductUtil,
  isObjectValid,
} from "@estaly/ui";
import UnvalidLead from "@/components/subscription/UnvalidLead.vue";
import Header from "@/components/subscription/Header.vue";
import MarketingSection from "@/components/subscription/MarketingSection.vue";
import OfferPriceOption from "@/components/subscription/OfferPriceOption.vue";

export default {
  components: {
    Selector,
    MarketingSection,
    Header,
    UnvalidLead,
    RadioGroup,
    RadioGroupLabel,
    VeeForm,
    FormField,
    StripePayment,
    Loading,
    Footer,
    CustomerAgreement,
    GoogleAddress,
    OfferPriceOption,
  },

  data() {
    return {
      selectedContractSku: "",
      offerPrices: [],
      form: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: {
          street: "",
          city: "",
          province: "",
          country: "",
          zipCode: "",
          other: "",
        },
        buyerRole: "",
        companyName: "",
        sirenNumber: "",
        legalForm: "",
      },
    };
  },

  async created() {
    await this.withLoader(() => this.fetch(this.$route.params.uniqueToken));
    if (!this.unvalidLead) this.initForm();
  },

  watch: {
    offerPrices(newValue) {
      if (newValue.length) {
        this.$nextTick(() => {
          this.scrollToPayment();
        });
      }
    },
  },

  computed: {
    ...mapGetters("leads", ["lead", "unvalidLead", "logoUrl", "brokerLogoUrl", "isMpcpCategory", "isLeadConverted"]),

    productTitle() {
      return this.lead?.product?.title;
    },

    isVirtualProduct() {
      return isVirtualProductUtil(this.lead?.product);
    },

    marketing() {
      return this.lead?.marketingDetails?.marketing;
    },

    marketingDetails() {
      return this.lead?.marketingDetails?.marketingDetails;
    },

    marketingPage() {
      return this.lead?.marketingPage;
    },

    contractType() {
      return this.lead?.contractType;
    },

    monthlyPrice() {
      return this.offerPrices.find((op) => op.monthly === true)?.price;
    },

    selectedOfferPrice() {
      if (!this.selectedContractSku) return this.offerPrices[0] ?? null;
      return this.offerPrices.find((op) => op.contractSku === this.selectedContractSku) ?? this.offerPrices[0] ?? null;
    },

    monthDiscount() {
      return isObjectValid(this.lead?.discount?.type) ? this.lead.discount.type.split("_")[0] === "months" : false;
    },

    paymentLead() {
      return { ...this.lead, ...this.form };
    },

    legalFormOptions() {
      return ["sarl", "eurl", "sas", "sasu", "sa", "ei", "sca", "scs", "snc"].map((legalForm) => ({
        label: legalForm?.toUpperCase(),
        value: legalForm,
      }));
    },
  },

  methods: {
    ...mapActions("leads", ["fetch"]),
    ...mapActions("customer", ["withLoader"]),
    formatPrice,
    formatPriceTTC,

    initForm() {
      if (this.isLeadConverted) {
        this.redirectToThankYouPage();
        return;
      }

      this.form.firstName = this.lead.firstName;
      this.form.lastName = this.lead.lastName;
      this.form.email = this.lead.email;
      this.form.phone = this.lead.phone;
      this.form.address = this.lead.address
        ? JSON.parse(JSON.stringify(this.lead.address))
        : { street: "", city: "", province: "", country: "", zipCode: "", other: "" };
      this.form.buyerRole = this.lead.buyerRole;
      this.form.companyName = this.lead.companyName;
      this.form.sirenNumber = this.lead.sirenNumber;
      this.form.legalForm = this.lead.legalForm;
      this.offerPrices = this.lead.offerPrices;

      const recommendedOffer = this.lead.advisoryAnswer?.recommendedOffer;
      const recommendedOfferPrice = this.lead.offerPrices.find(
        (op) => recommendedOffer && op.offerMarketingTitle === recommendedOffer,
      );
      this.selectedContractSku = recommendedOfferPrice
        ? recommendedOfferPrice.contractSku
        : this.offerPrices[0].contractSku;
    },

    scrollToPayment() {
      const paymentElement = this.$refs.paymentElement;
      if (this.$route.query.scrollToPayment === "true") {
        if (paymentElement) paymentElement.scrollIntoView({ behavior: "smooth" });
      }
    },

    redirectToThankYouPage() {
      this.$router.push("/thank_you");
    },
  },
};
</script>
